import * as moment from 'moment-timezone';
import { memoize } from 'lodash-es';
import { Moment } from 'moment-timezone';
export const memoizedMoment = memoize(moment, (...args) => args.join('_'));

function _convertUTCDateToTimezoneMoment(
    date: string,
    tz: string
): moment.Moment {
    if (!date || !tz) {
        return null;
    }

    // Deserialize UTC date from backend
    let utcDate = moment.utc(date);

    if (!utcDate.isValid()) {
        return null;
    }

    // Convert UTC date to offer TZ to determine date values user originally selected
    let momentDate = utcDate.tz(tz);

    // Create a local TZ date that mirrors the offer TZ date values, without doing any TZ offset
    return moment({
        year: momentDate.year(),
        month: momentDate.month(),
        day: momentDate.date(),
        hour: momentDate.hour(),
        minute: momentDate.minute()
    });
}

function _convertUTCDateToTimezone(date: string, tz: string): Date {
    const timezoneDate: moment.Moment = _convertUTCDateToTimezoneMoment(
        date,
        tz
    );
    if (!timezoneDate) {
        return null;
    }
    return timezoneDate.toDate();
}

export const convertUTCDateToTimezone = memoize(
    _convertUTCDateToTimezone,
    (date, tz) => `${date}_${tz}`
);

export const convertUTCDateToTimezoneFormatted = (
    date: string,
    tz: string,
    dateFormat = 'MM/DD/YYYY hh:mm A zz'
) =>
    _convertUTCDateToTimezoneMoment(date, tz)
        ? _convertUTCDateToTimezoneMoment(date, tz).format(dateFormat)
        : '';

function _convertUTCDateToTimezoneWithSeconds(date: string, tz: string): Date {
    if (!date || !tz) {
        return null;
    }

    // Deserialize UTC date from backend
    let utcDate = moment.utc(date);

    if (!utcDate.isValid()) {
        return null;
    }

    // Convert UTC date to offer TZ to determine date values user originally selected
    let momentDate = utcDate.tz(tz);

    // Create a local TZ date that mirrors the offer TZ date values, without doing any TZ offset
    return moment({
        year: momentDate.year(),
        month: momentDate.month(),
        day: momentDate.date(),
        hour: momentDate.hour(),
        minute: momentDate.minute(),
        seconds: momentDate.seconds()
    }).toDate();
}

export const convertUTCDateToTimezoneWithSeconds = memoize(
    _convertUTCDateToTimezoneWithSeconds,
    (date, tz) => `${date}_${tz}`
);

/***
 * Convert a JS local date to configured timezone, clamping hour/minute to
 * specific values.
 * @return UTC timestamp with the above conversion
 */
export function convertDateToUTCTimestamp(
    date: Date,
    tz: string,
    hour: number,
    minute: number,
    second: number
): string {
    return moment
        .tz(
            {
                year: date.getFullYear(),
                month: date.getMonth(),
                day: date.getDate(),
                hour: hour,
                minute: minute,
                second: second
            },
            tz
        )
        .utc()
        .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
}

export function convertLocalDateToUTCTimestamp(
    date: Date,
    hour: number,
    minute: number,
    second: number
): string {
    return moment({
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        hour: hour,
        minute: minute,
        second: second
    })
        .utc()
        .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
}
